import {LevelLabelUI} from './LevelLabel';
import {ClientPos2D} from '../../ui/positions';
import {LABEL_HEIGHT_PX} from './LevelPlacer';

import { THREE } from "@adsk/lmv";

const PLAN_LIST_CLASS = 'hud-level-plan-list';
const PLAN_LIST_INNER_CLASS = 'hud-level-plan-list-inner';
/** Must match `.hud-level-plan-list-inner`'s `gap` in LevelsLayer.css. */
const GAP_PX = 12;

/** Owns the scrollable DOM column for 2D plan mode level labels */
export class PlanModeLevelPlacer {
    #appliedScrollPadding = 0;
    #availableHeight = 0;
    #pendingSelectedIndex = -1;
    // Tracks whether the selected level has been centered once on entering Plan Mode.
    #initialScrollDone = false;
    #resizeObserver;
    #boundStopIfScrollable;

    constructor(document, hostDom) {
        this.container = document.createElement('div');
        this.container.className = PLAN_LIST_CLASS;

        this.innerContainer = document.createElement('div');
        this.innerContainer.className = PLAN_LIST_INNER_CLASS;
        this.container.appendChild(this.innerContainer);

        hostDom.appendChild(this.container);

        // Prevent wheel/touch events from reaching the viewer while the list is scrollable,
        // so they aren't handled as camera zoom/pan.
        this.#boundStopIfScrollable = this.#stopIfScrollable.bind(this);
        for (const type of ['wheel', 'touchstart', 'touchmove', 'touchend', 'touchcancel']) {
            this.container.addEventListener(type, this.#boundStopIfScrollable);
        }

        // Re-syncs the layout whenever the browser reports a size change for the list.
        this.#resizeObserver = new ResizeObserver(() => this.#syncScrollLayout());
        this.#resizeObserver.observe(this.innerContainer);
    }

    dispose() {
        this.#resizeObserver.disconnect();
        this.container.remove();
    }

    /** Creates a label as a child in the scroll column's DOM flow. */
    createLabel(hud, layer, level, facetType) {
        const label = new LevelLabelUI(hud, layer, new ClientPos2D(new THREE.Vector2(0, 0)), level, facetType);
        label.init(this.innerContainer);

        // Let the scroll column's CSS flow position the label.
        label.dom.style.position = 'relative';
        label.dom.style.width = 'auto';
        label.dom.style.height = 'auto';
        label.innerDom.style.transform = 'none';

        // Use the label's rendered DOM rect for collision detection, relative to the Viewer canvas.
        const canvasWrap = hud.viewer.canvasWrap;
        const target = { ui: label };
        label.getCollisionTarget = () => {
            const canvasRect = canvasWrap.getBoundingClientRect();
            const rect = label.labelHost.getBoundingClientRect();
            target.x = rect.left - canvasRect.left;
            target.y = rect.top - canvasRect.top;
            target.width = rect.width;
            target.height = rect.height;
            return target;
        };

        return label;
    }

    /** 
     * Moves the scroll column's children into `orderedLabels`' order. 
     * Moves only labels that are out of order to preserve DOM state (such as `:hover`)
     */
    reorder(orderedLabels) {
        let currentChild = this.innerContainer.firstElementChild;
        for (const label of orderedLabels) {
            if (label.dom === currentChild) {
                currentChild = currentChild.nextElementSibling;
            } else {
                this.innerContainer.insertBefore(label.dom, currentChild);
            }
        }
    }

    /**
     * Sets the scroll area height and centers the list when it fits within the viewport.
     * If scrolling is needed, initially scrolls the selected level to the vertical center.
     */
    layout(viewportHeightPx, selectedIndex) {
        this.#availableHeight = viewportHeightPx;
        this.container.style.height = `${this.#availableHeight}px`;
        this.#pendingSelectedIndex = selectedIndex;

        // Finalize immediately to handle selection changes that don't resize the list.
        this.#syncScrollLayout();
    }

    /**
     * Syncs the scroll layout by updating overflow padding and setting the initial
     * scroll position when needed.
     */
    #syncScrollLayout() {
        const availableHeight = this.#availableHeight;

        // Exclude the applied scroll padding without changing the DOM, preserving the current scroll position.
        const naturalHeight = this.container.scrollHeight - 2 * this.#appliedScrollPadding;
        const overflowing = naturalHeight > availableHeight;

        // When it overflows, add enough top/bottom padding for the first/last level to still be
        // scrollable all the way to the vertical center of the viewport, not just to the edge.
        const scrollPadding = overflowing ? Math.max(0, (availableHeight - LABEL_HEIGHT_PX) / 2) : 0;
        if (scrollPadding !== this.#appliedScrollPadding) {
            this.innerContainer.style.paddingTop = `${scrollPadding}px`;
            this.innerContainer.style.paddingBottom = `${scrollPadding}px`;
            this.#appliedScrollPadding = scrollPadding;
        }

        // Set the initial scroll position only once per plan mode session.
        if (this.#initialScrollDone) return;

        if (!overflowing) {
            // Nothing to scroll to yet: keep the scroll at the top
            this.container.scrollTop = 0;
            return;
        }

        if (this.#pendingSelectedIndex < 0) {
            // No selection to center on yet — e.g. an async auto-select hasn't landed yet.
            // Start at the top until a selected level is available to center.
            this.container.scrollTop = scrollPadding;
            return;
        }

        // Compute the selected level's position from its index and scroll it to the viewport center.
        const selectedTop = scrollPadding + this.#pendingSelectedIndex * (LABEL_HEIGHT_PX + GAP_PX);
        const target = selectedTop + LABEL_HEIGHT_PX / 2 - availableHeight / 2;
        const maxScrollTop = this.container.scrollHeight - availableHeight;
        this.container.scrollTop = Math.min(Math.max(0, target), maxScrollTop);
        this.#initialScrollDone = true;
    }

    #stopIfScrollable(event) {
        if (this.container.scrollHeight > this.container.clientHeight) {
            event.stopPropagation();
        }
    }
}
